import React, { PropsWithChildren } from 'react';
import { i18n_add_to_my_x } from '@yi/i18n/phrases/i18n_add_to_my_x';
import { i18n_remove_from_my_x } from '@yi/i18n/phrases/i18n_remove_from_my_x';

import { analytics } from '~/common';
import { turquoise } from '~/styles';
import { CardType } from '~/types';

import { StyledBookmarkIcon } from './elements';
import { getMyLibraryContentType, PracticeType } from './getMyLibraryContentType';
import { MenuListButton } from './MenuListButton';
import { useAddRemoveFavorite } from '../hooks';
import { useLocale } from '../LocaleProvider';

const { sendAddToMyLibraryClicked, sendRemoveFromMyLibraryClicked } = analytics;
export interface FavoriteButtonProps {
  contentId: string;
  contentTitle?: string;
  isFavorite: boolean;
  isTeacher?: boolean;
  displayIcon?: boolean;
  cardType?: CardType;
  practiceType?: PracticeType;
  onRemove?: (contentId: string) => void;
}

export const FavoriteIcon: React.FC<PropsWithChildren<{ isFavorite: boolean }>> = ({
  isFavorite,
}) => {
  const dataTestId = isFavorite ? 'favorite-icon-active' : 'favorite-icon-inactive';
  const fillOpacity = isFavorite ? '1' : '0.5';
  const fillColor = isFavorite ? turquoise : 'black';
  return (
    <StyledBookmarkIcon data-testid={dataTestId} fillOpacity={fillOpacity} fillColor={fillColor} />
  );
};

export const FavoriteButton: React.FC<PropsWithChildren<FavoriteButtonProps>> = ({
  contentId,
  contentTitle,
  cardType,
  practiceType,
  isTeacher,
  displayIcon,
  onRemove,
  ...props
}) => {
  const { locale } = useLocale();
  const { addFavorite, removeFavorite, loading, ...hookData } = useAddRemoveFavorite({
    contentId,
    isTeacher,
    onRemove,
  });

  const contentType = getMyLibraryContentType({ cardType, practiceType, locale });
  const isFavorite = hookData.isFavorite || props.isFavorite;
  const buttonText = isFavorite
    ? i18n_remove_from_my_x(locale, { contentType })
    : i18n_add_to_my_x(locale, { contentType });

  return (
    <MenuListButton
      loading={loading}
      data-testid="favorite-button"
      aria-label="Toggle favorite"
      onClick={e => {
        e.preventDefault();
        if (loading) return;
        if (isFavorite) {
          removeFavorite();
          if (cardType) sendRemoveFromMyLibraryClicked({ title: contentTitle || '', cardType });
        } else {
          if (cardType) sendAddToMyLibraryClicked({ title: contentTitle || '', cardType });
          addFavorite();
        }
      }}
      Icon={<FavoriteIcon isFavorite={isFavorite} />}
      displayIcon={displayIcon}
      buttonText={buttonText}
    />
  );
};
